import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { extractLeadFields, fetchRecordingBlobUrl, getCall, getCallRecording, getCallTranscript } from "../api/calls.ts";
import { createLead } from "../api/leads.ts";
import { listSites } from "../api/sites.ts";

type LeadForm = {
  site_id: string; name: string; phone_e164: string; email: string;
  address_text: string; property_type: string; service_type: string;
  gate_operator_kind: string; job_details: string; scheduled_time: string;
  service_call_fee: string; notes: string; asap: boolean;
};

const EMPTY_FORM: LeadForm = {
  site_id: "", name: "", phone_e164: "", email: "", address_text: "",
  property_type: "", service_type: "", gate_operator_kind: "", job_details: "",
  scheduled_time: "", service_call_fee: "", notes: "", asap: false,
};

// ── localStorage helpers ──────────────────────────────────────────────────────
function storageKey(callId: string) { return `lead-form-${callId}`; }

function loadForm(callId: string): LeadForm | null {
  try {
    const raw = localStorage.getItem(storageKey(callId));
    if (!raw) return null;
    return { ...EMPTY_FORM, ...JSON.parse(raw) };
  } catch { return null; }
}

function saveForm(callId: string, form: LeadForm) {
  try { localStorage.setItem(storageKey(callId), JSON.stringify(form)); } catch { /* ignore */ }
}

function clearForm(callId: string) {
  try { localStorage.removeItem(storageKey(callId)); } catch { /* ignore */ }
}
// ─────────────────────────────────────────────────────────────────────────────

export default function CallDetailPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: call } = useQuery({ queryKey: ["call", id], queryFn: () => getCall(id), enabled: Boolean(id) });
  const { data: rec } = useQuery({ queryKey: ["call-recording", id], queryFn: () => getCallRecording(id), enabled: Boolean(id) && Boolean(call?.recording_id), retry: false });
  const { data: transcript } = useQuery({ queryKey: ["call-transcript", id], queryFn: () => getCallTranscript(id), enabled: Boolean(id), retry: false });
  const { data: sitesData } = useQuery({ queryKey: ["sites"], queryFn: listSites });

  const audioRef = useRef<HTMLAudioElement>(null);
  const [audioBlobUrl, setAudioBlobUrl] = useState<string | null>(null);
  const [audioError, setAudioError] = useState<string | null>(null);

  // ── Lead form state — restored from localStorage on mount ─────────────────
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadForm, setLeadForm] = useState<LeadForm>(EMPTY_FORM);
  const [extracting, setExtracting] = useState(false);
  const [leadError, setLeadError] = useState<string | null>(null);
  const [formReady, setFormReady] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Restore persisted form on mount
  useEffect(() => {
    if (!id) return;
    const saved = loadForm(id);
    if (saved) {
      setLeadForm(saved);
      setShowLeadForm(true);
    }
    setFormReady(true);
  }, [id]);

  // Auto-save to localStorage whenever form data changes.
  // Deliberately NOT gated on showLeadForm — saves whenever any field has data,
  // so closing/reopening the tab always restores correctly.
  useEffect(() => {
    if (!id || !formReady) return;
    const hasData = Object.values(leadForm).some(v => v !== "");
    if (hasData) saveForm(id, leadForm);
  }, [leadForm, id, formReady]);
  // ─────────────────────────────────────────────────────────────────────────

  const createLeadMutation = useMutation({
    mutationFn: createLead,
    onSuccess: (lead) => {
      clearForm(id);
      qc.invalidateQueries({ queryKey: ["leads"] });
      navigate(`/leads/${lead.id}`);
    },
    onError: (e: Error) => setLeadError(e.message),
  });

  const runExtraction = async (defaultSite: string) => {
    setExtracting(true);
    setLeadError(null);
    try {
      const ext = await extractLeadFields(id);
      const feeCents = ext.service_call_fee_cents;
      const filled: LeadForm = {
        site_id: defaultSite,
        name: ext.name ?? "",
        phone_e164: ext.phone_e164 ?? call?.from_e164 ?? "",
        email: "",
        address_text: ext.address_text ?? "",
        property_type: ext.property_type ?? "",
        service_type: ext.service_type ?? "",
        gate_operator_kind: ext.gate_operator_kind ?? "",
        job_details: ext.job_details ?? "",
        scheduled_time: ext.scheduled_time ? new Date(ext.scheduled_time).toISOString().slice(0, 16) : "",
        service_call_fee: feeCents != null ? (feeCents / 100).toFixed(2) : "",
        notes: ext.notes ?? (transcript?.summary ? `Call summary: ${transcript.summary}` : ""),
        asap: false,
      };
      setLeadForm(filled);
      saveForm(id, filled);
    } catch (e) {
      setLeadError(`Auto-fill failed: ${(e as Error).message}. Fill fields manually.`);
      setLeadForm(f => ({ ...f, notes: transcript?.summary ? `Call summary: ${transcript.summary}` : "" }));
    } finally {
      setExtracting(false);
    }
  };

  const openLeadForm = async () => {
    const saved = loadForm(id);
    if (saved) {
      setLeadForm(saved);
      setLeadError(null);
      setShowLeadForm(true);
      return;
    }

    const defaultSite = call?.site_id ?? sitesData?.[0]?.id ?? "";
    setLeadForm({ ...EMPTY_FORM, site_id: defaultSite, phone_e164: call?.from_e164 ?? "" });
    setLeadError(null);
    setShowLeadForm(true);
    await runExtraction(defaultSite);
  };

  // Save immediately on every keystroke — don't rely solely on the useEffect
  function setField<K extends keyof LeadForm>(k: K, v: LeadForm[K]) {
    setLeadForm(f => {
      const next = { ...f, [k]: v };
      if (id) saveForm(id, next);
      return next;
    });
  }

  function cancelForm() {
    setShowLeadForm(false);
    clearForm(id);
    setLeadForm(EMPTY_FORM);
  }

  function submitLead() {
    const feeCents = leadForm.service_call_fee ? Math.round(parseFloat(leadForm.service_call_fee) * 100) : undefined;
    createLeadMutation.mutate({
      site_id: leadForm.site_id,
      source: "call",
      name: leadForm.name || undefined,
      phone_e164: leadForm.phone_e164 || undefined,
      email: leadForm.email || undefined,
      address_text: leadForm.address_text || undefined,
      property_type: (leadForm.property_type as "residential" | "commercial") || undefined,
      service_type: leadForm.service_type || undefined,
      gate_operator_kind: leadForm.gate_operator_kind || undefined,
      job_details: leadForm.job_details || undefined,
      scheduled_time: leadForm.scheduled_time ? new Date(leadForm.scheduled_time).toISOString() : undefined,
      service_call_fee_cents: feeCents != null && !isNaN(feeCents) ? feeCents : undefined,
      notes: leadForm.notes || undefined,
      asap: leadForm.asap,
    });
  }

  useEffect(() => {
    if (!call?.recording_id || rec?.status !== "archived") return;
    let cancelled = false; let createdUrl: string | null = null;
    const load = async () => {
      try {
        const url = await fetchRecordingBlobUrl(id);
        if (cancelled) { URL.revokeObjectURL(url); return; }
        createdUrl = url; setAudioBlobUrl(url); setAudioError(null);
      } catch (e) { if (!cancelled) setAudioError(String((e as Error).message)); }
    };
    load();
    return () => { cancelled = true; if (createdUrl) URL.revokeObjectURL(createdUrl); };
  }, [id, call?.recording_id, rec?.status]);

  if (!call) return <div className="text-sm text-slate-500">Loading…</div>;

  const siteName = sitesData?.find(s => s.id === call.site_id)?.name;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="break-all text-2xl font-semibold">Call · {call.from_e164} → {call.to_e164}</h1>
        <div className="flex gap-2">
          {call.lead_id && (
            <button type="button" className="btn-secondary" onClick={() => navigate(`/leads/${call.lead_id}`)}>
              View Lead →
            </button>
          )}
          <button
            type="button"
            className="btn-secondary"
            disabled={refreshing}
            onClick={async () => {
              setRefreshing(true);
              await Promise.all([
                qc.invalidateQueries({ queryKey: ["call", id] }),
                qc.invalidateQueries({ queryKey: ["call-recording", id] }),
                qc.invalidateQueries({ queryKey: ["call-transcript", id] }),
              ]);
              setTimeout(() => setRefreshing(false), 800);
            }}
          >
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* Call details */}
      <section className="card grid gap-2 md:grid-cols-2">
        <Info label="Website" value={siteName ?? "—"} />
        <Info label="Direction" value={call.direction} />
        <Info label="Status" value={call.status.replace(/_/g, " ")} />
        <Info label="Started" value={new Date(call.started_at).toLocaleString()} />
        <Info label="Answered" value={call.answered_at ? new Date(call.answered_at).toLocaleString() : "—"} />
        <Info label="Ended" value={call.ended_at ? new Date(call.ended_at).toLocaleString() : "—"} />
        <Info label="Duration" value={call.duration_s != null ? `${call.duration_s}s` : "—"} />
        <Info label="From" value={call.from_e164} />
        <Info label="To" value={call.to_e164} />
        <Info label="Forwarded to" value={call.forwarded_to_e164 ?? "—"} />
        <Info label="Twilio CallSid" value={call.twilio_call_sid} mono />
      </section>

      {/* Recording */}
      <section className="card">
        <h2 className="mb-2 text-sm font-semibold uppercase text-slate-500">Recording</h2>
        {!call.recording_id && <p className="text-sm text-slate-500">No recording for this call.</p>}
        {call.recording_id && rec?.status === "pending" && <p className="text-sm text-amber-700">Recording archive in progress — refresh in a minute.</p>}
        {call.recording_id && rec?.status === "failed" && <p className="text-sm text-red-700">Recording archive failed.</p>}
        {call.recording_id && rec?.status === "archived" && !audioBlobUrl && !audioError && <p className="text-sm text-slate-500">Loading audio…</p>}
        {call.recording_id && rec?.status === "archived" && audioBlobUrl && (
          <audio ref={audioRef} controls src={audioBlobUrl} className="w-full" onError={() => setAudioError("playback failed")}>
            Your browser does not support audio.
          </audio>
        )}
        {audioError && <p className="mt-2 text-sm text-red-700">{audioError}</p>}
        {rec && <p className="mt-2 text-xs text-slate-500">{rec.duration_s != null ? `${rec.duration_s}s` : ""} {rec.size_bytes != null ? `· ${Math.round(rec.size_bytes / 1024)} KB` : ""}</p>}
      </section>

      {/* Create Lead trigger — below Recording, only if no lead yet and form not yet opened */}
      {!call.lead_id && !showLeadForm && (
        <div className="flex justify-center">
          <button
            type="button"
            className="btn-primary px-8 py-3 text-base shadow-md"
            onClick={openLeadForm}
          >
            + Create Lead from this Call
          </button>
        </div>
      )}

      {/* ── Inline lead form ── */}
      {showLeadForm && (
        <section className="card space-y-5 border-brand-200 bg-brand-50/30">
          <div className="flex items-center justify-between">
            <h2 className="section-title">New Lead from this Call</h2>
            {extracting ? (
              <span className="flex items-center gap-1.5 text-xs text-brand-600">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading transcript…
              </span>
            ) : (
              <button
                type="button"
                className="btn-secondary px-2 py-1 text-xs"
                onClick={() => runExtraction(leadForm.site_id || (call?.site_id ?? sitesData?.[0]?.id ?? ""))}
              >
                Re-extract from transcript
              </button>
            )}
          </div>

          <LField label="Site">
            <select className="input" value={leadForm.site_id} onChange={e => setField("site_id", e.target.value)}>
              {sitesData?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </LField>

          <div>
            <p className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-400">Contact</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <LField label="Caller Name" incomplete={!leadForm.name && !extracting}>
                <input className="input" placeholder="Full name" value={leadForm.name} onChange={e => setField("name", e.target.value)} />
              </LField>
              <LField label="Phone">
                <input className="input" value={leadForm.phone_e164} onChange={e => setField("phone_e164", e.target.value)} />
              </LField>
            </div>
          </div>

          <div>
            <p className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-400">Service Location</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <LField label="Address" className="sm:col-span-2" incomplete={!leadForm.address_text && !extracting}>
                <input className="input" placeholder="123 Main St, City, TX 77001" value={leadForm.address_text} onChange={e => setField("address_text", e.target.value)} />
              </LField>
              <LField label="Property Type" incomplete={!leadForm.property_type && !extracting}>
                <select className="input" value={leadForm.property_type} onChange={e => setField("property_type", e.target.value)}>
                  <option value="">— Select —</option>
                  <option value="residential">Residential</option>
                  <option value="commercial">Commercial</option>
                </select>
              </LField>
              <LField label="Service Type" incomplete={!leadForm.service_type && !extracting}>
                <input className="input" placeholder="e.g. Gate repair" value={leadForm.service_type} onChange={e => setField("service_type", e.target.value)} />
              </LField>
            </div>
          </div>

          <div>
            <p className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-400">Job Intake</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <LField label="Gate & Operator Kind" className="sm:col-span-2" incomplete={!leadForm.gate_operator_kind && !extracting}>
                <input className="input" placeholder="e.g. LiftMaster CSL24V on swing gate" value={leadForm.gate_operator_kind} onChange={e => setField("gate_operator_kind", e.target.value)} />
              </LField>
              <LField label="Job Details" className="sm:col-span-2" incomplete={!leadForm.job_details && !extracting}>
                <textarea className="input" rows={3} placeholder="Describe the issue or work needed…" value={leadForm.job_details} onChange={e => setField("job_details", e.target.value)} />
              </LField>
              <LField label="Scheduled Time">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => { if (leadForm.asap) { setField("asap", false); } else { setField("asap", true); setField("scheduled_time", ""); } }}
                    className={`shrink-0 rounded-md border px-3 py-2 text-xs font-semibold transition-colors ${
                      leadForm.asap
                        ? "border-amber-400 bg-amber-100 text-amber-800"
                        : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    ⚡ ASAP
                  </button>
                  <input
                    className={`input flex-1 ${leadForm.asap ? "opacity-40" : ""}`}
                    type="datetime-local"
                    disabled={leadForm.asap}
                    value={leadForm.asap ? "" : leadForm.scheduled_time}
                    onChange={e => { setField("asap", false); setField("scheduled_time", e.target.value); }}
                  />
                </div>
              </LField>
              <LField label="Service Call Fee ($)">
                <div className="relative">
                  <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-slate-400">$</span>
                  <input className="input pl-7" type="number" min="0" step="0.01" placeholder="0.00" value={leadForm.service_call_fee} onChange={e => setField("service_call_fee", e.target.value)} />
                </div>
              </LField>
            </div>
          </div>

          <LField label="Internal Notes">
            <textarea className="input" rows={2} placeholder="Notes for your team…" value={leadForm.notes} onChange={e => setField("notes", e.target.value)} />
          </LField>

          {leadError && <p className="text-sm text-red-600">{leadError}</p>}

          <div className="flex justify-end gap-2 border-t border-slate-200 pt-4">
            <button className="btn-secondary" onClick={cancelForm}>Cancel</button>
            <button className="btn-primary" disabled={!leadForm.site_id || createLeadMutation.isPending || extracting} onClick={submitLead}>
              {createLeadMutation.isPending ? "Creating…" : "Create Lead"}
            </button>
          </div>
        </section>
      )}

      {/* Transcript */}
      <section className="card">
        <h2 className="mb-2 text-sm font-semibold uppercase text-slate-500">Transcript &amp; AI Analysis</h2>
        {!transcript && <p className="text-sm text-slate-500">No transcript yet.</p>}
        {transcript?.status === "pending" && <p className="text-sm text-amber-700">Waiting for Claude analysis…</p>}
        {transcript?.status === "processing" && <p className="text-sm text-amber-700">Analyzing…</p>}
        {transcript?.status === "failed" && <p className="text-sm text-red-700">Analysis failed.</p>}
        {transcript?.raw_text && (
          <div className="space-y-3">
            {transcript.sentiment && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Sentiment</span>
                <SentimentBadge sentiment={transcript.sentiment} />
              </div>
            )}
            {transcript.summary && (
              <div>
                <p className="text-xs font-medium uppercase text-slate-500">Summary</p>
                <p className="mt-1 text-sm">{transcript.summary}</p>
              </div>
            )}
            {transcript.action_items && transcript.action_items.length > 0 && (
              <div>
                <p className="text-xs font-medium uppercase text-slate-500">Action items</p>
                <ul className="mt-1 list-inside list-disc space-y-0.5 text-sm">
                  {transcript.action_items.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              </div>
            )}
            <details>
              <summary className="cursor-pointer text-xs text-slate-500 hover:text-slate-700">Raw transcript</summary>
              <pre className="mt-2 whitespace-pre-wrap rounded bg-slate-50 p-3 text-xs text-slate-700">{transcript.raw_text}</pre>
            </details>
          </div>
        )}
      </section>
    </div>
  );
}

function LField({ label, children, className = "", incomplete = false }: {
  label: string; children: React.ReactNode; className?: string; incomplete?: boolean;
}) {
  return (
    <div className={`field ${incomplete ? "field-incomplete" : ""} ${className}`}>
      <label className="field-label">
        {label}
        {incomplete && <span className="ml-1.5 text-[10px] font-semibold normal-case tracking-normal text-red-500">· missing</span>}
      </label>
      {children}
    </div>
  );
}

function SentimentBadge({ sentiment }: { sentiment: string }) {
  const map: Record<string, string> = { positive: "bg-green-100 text-green-800", neutral: "bg-slate-100 text-slate-700", negative: "bg-red-100 text-red-800", urgent: "bg-orange-100 text-orange-800" };
  return <span className={`rounded px-2 py-0.5 text-xs font-medium capitalize ${map[sentiment] ?? "bg-slate-100 text-slate-700"}`}>{sentiment}</span>;
}

function Info({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5 text-sm sm:flex-row sm:justify-between sm:gap-4">
      <span className="text-slate-500">{label}</span>
      <span className={`break-all ${mono ? "font-mono text-xs" : "font-medium"} sm:text-right`}>{value}</span>
    </div>
  );
}
